import { useEffect, useState, useRef } from "react";
import { Navbar } from "@/components/Navbar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatTRY, STATUS_META, STATUS_ORDER, DEFAULT_PRICES, enrich3dConfig } from "@/lib/pricing";
import Wardrobe3D from "@/components/Wardrobe3D";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, DollarSign, Layers, TrendingUp, MapPin, Phone, Tag, Save, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { generateCncProductionPdf, downloadCutListCsv } from "@/lib/pdf";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState(DEFAULT_PRICES);
  const [savingPrices, setSavingPrices] = useState(false);
  const [history, setHistory] = useState([]);
  const [track, setTrack] = useState({});
  const [render3dOrder, setRender3dOrder] = useState(null); // 3D görüntüsü alınan sipariş
  const captureRef = useRef(null);

  // Siparişin 3D modelini gizli alanda render edip PNG olarak yakala (PDF'e eklemek için)
  const capture3dImage = async (order) => {
    setRender3dOrder(order);
    await new Promise((r) => setTimeout(r, 3200)); // dokular + ilk kareler otursun
    let imageData = null;
    try {
      const canvas = captureRef.current?.querySelector("canvas");
      if (canvas) imageData = canvas.toDataURL("image/png");
    } catch (e) {
      /* WebGL yakalanamazsa görsel olmadan devam */
    }
    setRender3dOrder(null);
    return imageData;
  };

  const setTrackField = (id, field, val) =>
    setTrack((t) => ({ ...t, [id]: { ...t[id], [field]: val } }));

  const load = () => {
    Promise.all([api.get("/admin/orders"), api.get("/admin/stats")])
      .then(([o, s]) => {
        setOrders(o.data);
        setStats(s.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/prices").then(({ data }) => setPrices(data)).catch(() => {});
    api.get("/admin/price-history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  const setPrice = (group, key, val) => {
    const num = val === "" ? "" : Number(val);
    setPrices((p) => {
      if (group) return { ...p, [group]: { ...p[group], [key]: num } };
      return { ...p, [key]: num };
    });
  };

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      const { data } = await api.patch("/admin/prices", prices);
      setPrices(data);
      toast.success("Fiyatlar güncellendi.");
      api.get("/admin/price-history").then(({ data }) => setHistory(data)).catch(() => {});
    } catch {
      toast.error("Fiyatlar kaydedilemedi");
    } finally {
      setSavingPrices(false);
    }
  };

  const deleteOrder = async (id, orderNumber) => {
    if (!window.confirm(`${orderNumber} numaralı sipariş kalıcı olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success("Sipariş silindi");
      api.get("/admin/stats").then(({ data }) => setStats(data));
    } catch {
      toast.error("Sipariş silinemedi");
    }
  };

  const updateStatus = async (id, status) => {
    const t = track[id] || {};
    const payload = { status };
    if (status === "kargoda") {
      payload.trackingNumber = t.trackingNumber || "";
      payload.carrier = t.carrier || "";
    }
    try {
      const { data } = await api.patch(`/admin/orders/${id}/status`, payload);
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
      toast.success(`Durum güncellendi: ${STATUS_META[status].label}`);
      api.get("/admin/stats").then(({ data }) => setStats(data));
    } catch {
      toast.error("Durum güncellenemedi");
    }
  };

  const statCards = stats
    ? [
        { icon: Package, label: "Toplam Sipariş", value: stats.totalOrders, color: "#B07D48" },
        { icon: DollarSign, label: "Toplam Ciro", value: formatTRY(stats.revenue), color: "#10B981" },
        { icon: Layers, label: "Üretimde", value: stats.byStatus["üretimde"] || 0, color: "#3B82F6" },
        { icon: TrendingUp, label: "Teslim Edildi", value: stats.byStatus["teslim_edildi"] || 0, color: "#8B5CF6" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-atelier">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-4xl font-bold text-stone-900 mb-1">Yönetim Paneli</h1>
        <p className="text-stone-600 mb-6">Atölye siparişleri ve üretim durumu yönetimi.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((c, i) => (
            <div key={i} className="card-atelier rounded-2xl p-5" data-testid={`admin-stat-${i}`}>
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ background: `${c.color}1a`, color: c.color }}
              >
                <c.icon className="w-5 h-5" />
              </div>
              <p className="text-xs text-stone-500 font-mono uppercase">{c.label}</p>
              <p className="text-2xl font-bold text-stone-900 mt-0.5">{c.value}</p>
            </div>
          ))}
        </div>

        {/* Price management */}
        <div className="card-atelier rounded-2xl p-6 mb-8" data-testid="admin-prices-panel">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" />
              <h2 className="font-serif text-2xl font-bold text-stone-900">Malzeme Fiyat Yönetimi</h2>
            </div>
            <Button data-testid="admin-save-prices-btn" onClick={savePrices} disabled={savingPrices} className="rounded-full">
              <Save className="w-4 h-4" /> Fiyatları Kaydet
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-semibold text-stone-800 mb-2">Gövde (₺/m²)</p>
              <div className="space-y-2">
                {[["suntalam", "Suntalam"], ["mdf", "MDF"]].map(([id, label]) => (
                  <div key={id} className="flex items-center gap-3">
                    <Label className="w-28 text-sm">{label}</Label>
                    <Input
                      data-testid={`admin-price-body-${id}`}
                      type="number"
                      value={prices.body?.[id] ?? ""}
                      onChange={(e) => setPrice("body", id, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-stone-800 mb-2">Kapak (₺/m²)</p>
              <div className="space-y-2">
                {[["suntalam", "Suntalam"], ["mdf", "MDF (Mat)"], ["highgloss", "High Gloss"], ["akrilik", "Akrilik"], ["cam", "Cam (Siyah Alüminyum)"], ["ayna", "Ayna"]].map(([id, label]) => (
                  <div key={id} className="flex items-center gap-3">
                    <Label className="w-28 text-sm">{label}</Label>
                    <Input
                      data-testid={`admin-price-door-${id}`}
                      type="number"
                      value={prices.door?.[id] ?? ""}
                      onChange={(e) => setPrice("door", id, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-6 pt-6 border-t border-[#E6DFD5]">
            {[
              ["backPanel", "Arka Panel (₺/m²)"],
              ["edgeBand", "Kenar Bandı (₺/m)"],
              ["hinge", "Menteşe (₺/ad)"],
              ["handle", "Kulp (₺/ad)"],
              ["cncRate", "CNC (₺/m²)"],
              ["drawerRail", "Çekmece Rayı (₺/ad)"],
              ["legUnit", "Ayak (₺/ad)"],
            ].map(([id, label]) => (
              <div key={id}>
                <Label className="text-xs">{label}</Label>
                <Input
                  data-testid={`admin-price-${id}`}
                  type="number"
                  value={prices[id] ?? ""}
                  onChange={(e) => setPrice(null, id, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Price history */}
        {history.length > 0 && (
          <div className="card-atelier rounded-2xl p-6 mb-8" data-testid="admin-price-history">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4">Fiyat Değişiklik Geçmişi</h2>
            <div className="max-h-56 overflow-y-auto divide-y divide-[#E6DFD5]">
              {history.map((h, i) => (
                <div key={i} className="py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-stone-500 font-mono text-xs">
                    {new Date(h.at).toLocaleString("tr-TR")} • {h.adminEmail}
                  </span>
                  <span className="text-stone-700 font-mono text-xs">
                    Gövde: Suntalam {h.prices?.body?.suntalam} / MDF {h.prices?.body?.mdf} &nbsp;|&nbsp;
                    Kapak: Sun {h.prices?.door?.suntalam} / MDF {h.prices?.door?.mdf} / HG {h.prices?.door?.highgloss} / Akr {h.prices?.door?.akrilik}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card-atelier rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-[#E6DFD5]">
            <h2 className="font-serif text-2xl font-bold text-stone-900">Siparişler</h2>
          </div>

          {loading ? (
            <p className="p-6 text-stone-500">Yükleniyor...</p>
          ) : orders.length === 0 ? (
            <p className="p-6 text-stone-500">Henüz sipariş yok.</p>
          ) : (
            <div className="divide-y divide-[#E6DFD5]" data-testid="admin-orders-list">
              {orders.map((o) => {
                const meta = STATUS_META[o.status] || STATUS_META.hazırlanıyor;
                return (
                  <div key={o.id} className="p-5 hover:bg-[#FDFBF7] transition-colors" data-testid={`admin-order-${o.orderNumber}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono text-sm font-bold text-stone-900">{o.orderNumber}</span>
                          <span
                            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                            style={{ background: meta.bg, color: meta.color }}
                          >
                            {meta.label}
                          </span>
                        </div>
                        <p className="text-xs text-stone-500 mt-1">{fmtDate(o.createdAt)}</p>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-stone-600">
                          <span>{o.userName || o.userEmail}</span>
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {o.shipping?.phone}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.shipping?.city}</span>
                        </div>
                        <p className="text-xs text-stone-500 mt-1">
                          {o.config?.width}×{o.config?.height}×{o.config?.depth} cm • {o.config?.shelves} raf •{" "}
                          {o.config?.doorless ? "kapaksız" : `${o.config?.doors} kapak`} • Gövde: {o.config?.bodyColorLabel}, Kapak: {o.config?.doorColorLabel}
                        </p>
                        <p className="text-xs text-stone-400 mt-1">
                          CNC: {o.cnc?.sheetCount} tabaka • fire %{o.cnc?.firePercent}
                        </p>
                        {o.status !== "iptal" && (
                          <button
                            data-testid={`admin-cnc-pdf-${o.orderNumber}`}
                            onClick={async (e) => {
                              const btnEl = e.currentTarget;
                              btnEl.disabled = true;
                              toast.info("3D model görüntüsü hazırlanıyor, üretim fişi oluşturuluyor...");
                              try {
                                const imageData = await capture3dImage(o);
                                await generateCncProductionPdf({
                                  config: enrich3dConfig(o.config),
                                  cost: { items: o.costItems || [], total: o.totalCost },
                                  cnc: o.cnc,
                                  orderNumber: o.orderNumber,
                                  imageData,
                                });
                                toast.success("CNC üretim fişi indirildi.");
                              } catch (err) {
                                toast.error("CNC üretim fişi oluşturulamadı.");
                              } finally {
                                btnEl.disabled = false;
                              }
                            }}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-stone-700 transition-colors"
                          >
                            <FileDown className="w-3.5 h-3.5" /> CNC Üretim Fişi (PDF)
                          </button>
                        )}
                        {o.status !== "iptal" && (
                          <button
                            data-testid={`admin-cnc-csv-${o.orderNumber}`}
                            onClick={() => {
                              try {
                                downloadCutListCsv({ config: o.config, orderNumber: o.orderNumber });
                              } catch (e) {
                                toast.error("Kesim listesi oluşturulamadı.");
                              }
                            }}
                            className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-full border border-stone-900 text-stone-900 text-xs font-semibold px-3 py-1.5 hover:bg-stone-100 transition-colors"
                          >
                            <FileDown className="w-3.5 h-3.5" /> Kesim Listesi (CSV)
                          </button>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        <span className="font-serif text-xl font-bold text-primary">{formatTRY(o.totalCost)}</span>
                        {o.status === "ödeme_bekliyor" ? (
                          <span
                            data-testid={`admin-order-pending-${o.orderNumber}`}
                            className="text-xs font-semibold text-stone-500 bg-stone-100 rounded-full px-3 py-1.5"
                          >
                            Ödeme Bekliyor
                          </span>
                        ) : (
                          <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                            <SelectTrigger data-testid={`admin-order-status-select-${o.orderNumber}`} className="w-[160px] rounded-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_ORDER.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`admin-status-option-${s}`}>
                                  {STATUS_META[s].label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <button
                          data-testid={`admin-order-delete-${o.orderNumber}`}
                          onClick={() => deleteOrder(o.id, o.orderNumber)}
                          className="text-stone-400 hover:text-red-600 transition-colors p-1"
                          title="Siparişi Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Kargo takip — yalnızca ödemesi alınmış siparişlerde */}
                    {o.paymentStatus === "ödendi" && (
                    <div className="mt-3 pt-3 border-t border-[#F0EAE2] flex flex-wrap items-end gap-2">
                      <div>
                        <Label className="text-[11px] text-stone-500">Kargo Firması</Label>
                        <Input
                          data-testid={`admin-carrier-${o.orderNumber}`}
                          className="h-9 w-40"
                          placeholder="Örn. Yurtiçi Kargo"
                          value={track[o.id]?.carrier ?? o.carrier ?? ""}
                          onChange={(e) => setTrackField(o.id, "carrier", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-stone-500">Takip No</Label>
                        <Input
                          data-testid={`admin-tracking-${o.orderNumber}`}
                          className="h-9 w-44"
                          placeholder="Takip numarası"
                          value={track[o.id]?.trackingNumber ?? o.trackingNumber ?? ""}
                          onChange={(e) => setTrackField(o.id, "trackingNumber", e.target.value)}
                        />
                      </div>
                      <Button
                        data-testid={`admin-ship-${o.orderNumber}`}
                        size="sm"
                        variant="outline"
                        className="rounded-full h-9"
                        onClick={() => updateStatus(o.id, "kargoda")}
                      >
                        Kargoya Ver & Bildir
                      </Button>
                      {o.trackingNumber && (
                        <span className="text-xs text-stone-500 ml-auto">
                          Mevcut: <span className="font-mono">{o.carrier} {o.trackingNumber}</span>
                        </span>
                      )}
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* CNC üretim fişi için gizli 3D yakalama alanı */}
      {render3dOrder && (
        <div
          ref={captureRef}
          data-testid="admin-3d-capture"
          style={{ position: "fixed", left: -10000, top: 0, width: 1200, height: 620, pointerEvents: "none" }}
          aria-hidden="true"
        >
          <Wardrobe3D config={enrich3dConfig(render3dOrder.config)} openAllSignal={false} dpr={2} captureMode />
        </div>
      )}
    </div>
  );
}
